import { Edit } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { DataGridPro, GridColDef, GridActionsCellItem, GridRowParams } from '@mui/x-data-grid-pro';
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, NavLink as RouterLink } from 'react-router-dom';

import { If } from 'app/components/if';
import { GeneralConstants } from 'app/constants/general-constants';
import { useCallbackOnProdSwitch } from 'app/hooks';
import { CleanPattern } from 'app/model/cris/clean-patterns';
import { cleanPatternsService } from 'app/services/clean-patterns.service';
import { dialogService } from 'app/services/dialog.service';
import { messagingService } from 'app/services/messaging.service';

import { selectProdDataStatus } from '../cris.slice';

export const CleanPatternsListing: React.FC = () => {
    const [rows, setRows] = useState<CleanPattern[]>([]);
    const [loading, setLoading] = useState(false);
    const showProd = useSelector(selectProdDataStatus);
    const history = useHistory();

    const loadPatterns = useCallback(() => {
        setLoading(true);
        cleanPatternsService.listAll({ pageSize: GeneralConstants.PAGE_SIZE_HUGE }).subscribe({
            next: (data) => setRows(data),
            error: () => messagingService.showError('Failed to load clean patterns'),
            complete: () => setLoading(false),
        });
    }, []);

    useCallbackOnProdSwitch(loadPatterns);
    useEffect(() => {
        loadPatterns();
    }, [loadPatterns]);

    const handleEdit = (pattern: CleanPattern) => {
        history.push(`/cris/clean-patterns/${pattern.id}/edit`);
    };

    const deployToProd = (): void => {
        dialogService.showConfirm(
            {
                message: 'Are you sure you want to deploy to production?',
                confirmText: 'Deploy',
            },
            () =>
                cleanPatternsService.deploy().subscribe(() => {
                    messagingService.showSuccess('Deployed');
                    loadPatterns();
                }),
        );
    };

    const columns: GridColDef[] = [
        { field: 'source', headerName: 'Source', flex: 1 },
        { field: 'version', headerName: 'Version', flex: 1 },
        { field: 'substr', headerName: 'Substring', flex: 1 },
        { field: 'regex', headerName: 'Regex', flex: 1 },
        !showProd && {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 80,
            getActions: (params: GridRowParams<CleanPattern>) => [
                <GridActionsCellItem icon={<Edit />} label='Edit' onClick={() => handleEdit(params.row)} />,
            ],
        },
    ].filter(Boolean) as GridColDef[];

    return (
        <>
            <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
                <Typography variant='h5'>Manage Clean Patterns</Typography>
                <If condition={!showProd}>
                    <Box sx={{ display: 'flex' }}>
                        <Button
                            variant='outlined'
                            color='warning'
                            component={RouterLink}
                            to='/cris/clean-patterns/new/create'
                            sx={{ marginRight: '8px' }}
                        >
                            Create Clean Pattern
                        </Button>
                        <Button variant='contained' color='primary' onClick={deployToProd}>
                            Deploy to Prod
                        </Button>
                    </Box>
                </If>
            </Box>

            <DataGridPro
                rows={rows}
                columns={columns}
                loading={loading}
                autoHeight
                density='compact'
                disableRowSelectionOnClick
                pagination
            />
        </>
    );
};
